<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Background overlay -->
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
    
    <!-- Modal container -->
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      
      <!-- Modal panel -->
      <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex flex-col items-center justify-center py-8">
          <div class="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
          <p class="text-gray-600 dark:text-gray-400">Loading CV details...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="flex flex-col items-center justify-center py-8">
          <svg class="w-12 h-12 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Failed to load CV</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-4">{{ error }}</p>
          <button @click="loadCvData" class="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
            Try Again
          </button>
        </div>

        <!-- Modal Content -->
        <div v-else>
          <!-- Header -->
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                Duplicate CV
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Create a copy of "{{ originalCvTitle }}" with new metadata
              </p>
            </div>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Form -->
          <form @submit.prevent="duplicateCv" class="space-y-6">
            <!-- CV Title -->
            <div>
              <label for="title" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New CV Title <span class="text-red-500">*</span>
              </label>
              <input 
                id="title"
                v-model="newCvData.title" 
                type="text" 
                required
                placeholder="e.g., Data Scientist CV - Microsoft Application"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <!-- Target Role -->
            <div>
              <label for="target_role" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Role <span class="text-red-500">*</span>
              </label>
              <input 
                id="target_role"
                v-model="newCvData.target_role" 
                type="text" 
                required
                placeholder="e.g., Data Scientist"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <!-- Target Company -->
            <div>
              <label for="target_company" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target Company
              </label>
              <input 
                id="target_company"
                v-model="newCvData.target_company" 
                type="text" 
                placeholder="e.g., Microsoft (optional)"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <!-- Description -->
            <div>
              <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                id="description"
                v-model="newCvData.description" 
                rows="3"
                placeholder="Brief description of this CV's purpose..."
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              ></textarea>
            </div>

            <!-- Original CV Info (Read-only) -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Source CV</h4>
              <div class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <p><span class="font-medium">Title:</span> {{ originalCvTitle }}</p>
                <p v-if="originalCvData.target_role"><span class="font-medium">Target Role:</span> {{ originalCvData.target_role }}</p>
                <p v-if="originalCvData.target_company"><span class="font-medium">Target Company:</span> {{ originalCvData.target_company }}</p>
                <p v-if="originalCvData.description"><span class="font-medium">Description:</span> {{ originalCvData.description }}</p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button 
                type="button" 
                @click="closeModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button 
                type="submit"
                :disabled="isDuplicating || !isFormValid"
                class="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <span v-if="isDuplicating" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                <span>{{ isDuplicating ? 'Duplicating...' : 'Duplicate CV' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { reportUpgradeRequired } from '../services/ApiService.js'

export default {
  name: 'CvDuplicateModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    cvId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['close', 'duplicated'],
  setup(props, { emit }) {
    const isLoading = ref(false)
    const isDuplicating = ref(false)
    const error = ref(null)
    
    const originalCvData = ref({})
    const originalCvTitle = ref('')
    
    const newCvData = ref({
      title: '',
      description: '',
      target_role: '',
      target_company: ''
    })

    // CSRF token helper
    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : null
    }

    // Form validation
    const isFormValid = computed(() => {
      return newCvData.value.title.trim().length > 0 && 
             newCvData.value.target_role.trim().length > 0
    })

    // Load CV data when modal opens
    const loadCvData = async () => {
      if (!props.cvId) return

      try {
        isLoading.value = true
        error.value = null

        const response = await fetch(`/api/cvs/${props.cvId}`, {
          headers: {
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()
        
        if (data.success) {
          originalCvData.value = {
            title: data.cv.title || '',
            description: data.cv.description || '',
            target_role: data.cv.target_role || '',
            target_company: data.cv.target_company || ''
          }
          originalCvTitle.value = data.cv.title || 'Untitled CV'
          
          // Pre-populate new CV data with modified versions of original
          newCvData.value = {
            title: `Copy of ${originalCvData.value.title}`,
            description: originalCvData.value.description,
            target_role: originalCvData.value.target_role,
            target_company: originalCvData.value.target_company
          }
        } else {
          throw new Error(data.message || 'Failed to load CV')
        }
      } catch (err) {
        console.error('Error loading CV data:', err)
        error.value = err.message
      } finally {
        isLoading.value = false
      }
    }

    // Duplicate CV
    const duplicateCv = async () => {
      if (!isFormValid.value) return

      try {
        isDuplicating.value = true

        const response = await fetch(`/api/cvs/${props.cvId}/duplicate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': getCSRFToken()
          },
          body: JSON.stringify({
            cv: newCvData.value
          })
        })

        if (response.status === 402) {
          const data = await response.json().catch(() => ({}))
          closeModal()
          reportUpgradeRequired(data)
          return
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()

        if (data.success) {
          emit('duplicated', data.cv)
          closeModal()
        } else {
          throw new Error(data.message || 'Failed to duplicate CV')
        }
      } catch (err) {
        console.error('Error duplicating CV:', err)
        error.value = err.message
      } finally {
        isDuplicating.value = false
      }
    }

    // Close modal
    const closeModal = () => {
      emit('close')
      // Reset form after a short delay to prevent visual glitch
      setTimeout(() => {
        newCvData.value = {
          title: '',
          description: '',
          target_role: '',
          target_company: ''
        }
        originalCvData.value = {}
        originalCvTitle.value = ''
        error.value = null
      }, 300)
    }

    // Watch for modal opening to load data
    watch(() => props.isOpen, (isOpen) => {
      if (isOpen && props.cvId) {
        loadCvData()
      }
    })

    return {
      isLoading,
      isDuplicating,
      error,
      originalCvData,
      originalCvTitle,
      newCvData,
      isFormValid,
      loadCvData,
      duplicateCv,
      closeModal
    }
  }
}
</script>

<style scoped>
/* Modal animations and styles handled by Tailwind classes */
</style>